/**
 * Short manager-facing labels for rollups and reports (not raw factor keys).
 */

const { FACTOR_KEYS } = require('./config/riskFactorRegistry');
const interactionRules = require('./interactions/interactionRulesConfig');

const PHRASE_BY_KEY = Object.fromEntries(
  (interactionRules.rules || []).map((r) => [r.interactionKey, r.patternPhrase || ''])
);

const FACTOR_LABELS = {
  total_company_tenure: 'Long time at the company',
  time_in_current_role: 'Time in current role',
  time_since_last_promotion: 'Time since last promotion',
  time_since_last_comp_change: 'Time since last pay change',
  career_velocity_vs_peers: 'Career pace vs peers',
  tenure_band: 'Career stage with the company',
  salary_vs_market_midpoint: 'Pay compared to market',
  salary_vs_internal_peers: 'Pay compared to internal peers',
  compensation_growth_rate: 'How pay has been growing',
  bonus_incentive_consistency: 'Bonus and incentives',
  equity_lti_value: 'Equity and long-term pay',
  total_rewards_competitiveness: 'Total rewards vs market',
  external_demand_for_role: 'Outside demand for this role',
  skill_transferability: 'How transferable their skills are',
  internal_mobility_availability: 'Internal move options',
  role_criticality: 'How critical the role is',
  role_scope_stability: 'Role scope stability',
  promotion_path_clarity: 'Clarity of promotion path',
  performance_rating_trend: 'Performance trend',
  performance_reward_mismatch: 'Performance vs rewards',
  recognition_frequency: 'Recognition and visibility',
  workload_pressure: 'Workload and pressure',
  performance_volatility: 'Performance ups and downs',
  manager_tenure: 'Manager stability',
  manager_change_frequency: 'How often managers change',
  manager_span_of_control: 'Manager span of control',
  manager_team_attrition_history: 'Turnover under this manager',
  manager_performance_stability: 'Manager performance stability',
  manager_engagement_signal: 'Manager engagement',
  team_attrition_rate: 'Team turnover rate',
  team_attrition_trend: 'Team turnover trend',
  average_team_tenure: 'Average tenure on the team',
  team_tenure_dispersion: 'Mix of tenure on the team',
  team_internal_mobility_rate: 'Team internal moves',
  team_workload_pressure: 'Team workload',
  high_risk_cluster_density: 'Clusters of pay or risk on the team',
  org_change_exposure: 'Exposure to org change',
  reorg_frequency: 'How often reorgs happen',
  leadership_change_exposure: 'Leadership change exposure',
  budget_pressure_signal: 'Budget pressure',
  location_site_instability: 'Site or location stability',
  absenteeism_change: 'Absence pattern changes',
  productivity_change: 'Productivity changes',
  collaboration_drop: 'Collaboration dropping',
  manager_checkin_drop: 'Manager check-ins dropping',
  local_market_hiring_pressure: 'Local hiring competition',
  competitor_talent_pull: 'Competitors hiring similar roles',
};

function titleCaseHumanized(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function managerFactorLabel(factorKey) {
  if (!factorKey) return '';
  if (FACTOR_LABELS[factorKey]) return FACTOR_LABELS[factorKey];
  if (FACTOR_KEYS.includes(factorKey)) return titleCaseHumanized(factorKey);
  return titleCaseHumanized(factorKey);
}

/**
 * List heading for interaction rollups: prefer plain phrase over raw key.
 */
function managerPatternTitle(interactionKey, patternPhrase) {
  const fromRule = PHRASE_BY_KEY[interactionKey];
  const phrase = (patternPhrase && String(patternPhrase).trim()) || fromRule || '';
  if (phrase) {
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
  }
  return titleCaseHumanized(interactionKey);
}

const CATEGORY_BY_FACTOR = {
  salary_vs_market_midpoint: 'compensation',
  salary_vs_internal_peers: 'compensation',
  compensation_growth_rate: 'compensation',
  bonus_incentive_consistency: 'compensation',
  equity_lti_value: 'compensation',
  total_rewards_competitiveness: 'compensation',
  time_since_last_comp_change: 'compensation',
  time_since_last_promotion: 'progression',
  career_velocity_vs_peers: 'progression',
  promotion_path_clarity: 'progression',
  internal_mobility_availability: 'progression',
  time_in_current_role: 'progression',
  external_demand_for_role: 'external_demand',
  skill_transferability: 'external_demand',
  local_market_hiring_pressure: 'external_demand',
  competitor_talent_pull: 'external_demand',
  manager_tenure: 'manager_team',
  manager_change_frequency: 'manager_team',
  manager_span_of_control: 'manager_team',
  manager_team_attrition_history: 'manager_team',
  manager_performance_stability: 'manager_team',
  manager_engagement_signal: 'manager_team',
  team_attrition_rate: 'manager_team',
  team_attrition_trend: 'manager_team',
  average_team_tenure: 'manager_team',
  team_tenure_dispersion: 'manager_team',
  team_internal_mobility_rate: 'manager_team',
  team_workload_pressure: 'manager_team',
  high_risk_cluster_density: 'manager_team',
  workload_pressure: 'workload',
  performance_volatility: 'workload',
  absenteeism_change: 'workload',
  productivity_change: 'workload',
  recognition_frequency: 'engagement',
  performance_reward_mismatch: 'engagement',
  performance_rating_trend: 'engagement',
  collaboration_drop: 'engagement',
  manager_checkin_drop: 'engagement',
  total_company_tenure: 'progression',
  tenure_band: 'progression',
  org_change_exposure: 'org_context',
  reorg_frequency: 'org_context',
  leadership_change_exposure: 'org_context',
  budget_pressure_signal: 'org_context',
  location_site_instability: 'org_context',
  role_criticality: 'org_context',
  role_scope_stability: 'org_context',
};

const PRIMARY_LABEL_BY_CATEGORY = {
  compensation: 'Compensation compression',
  progression: 'Promotion stagnation',
  external_demand: 'High external demand',
  manager_team: 'Manager or team instability',
  workload: 'Workload strain',
  engagement: 'Engagement decline',
  org_context: 'Organizational change pressure',
};

/** Factors derived from department/team aggregates — lower confidence for individual copy. */
const INFERRED_FACTOR_KEYS = new Set([
  'high_risk_cluster_density',
  'team_attrition_rate',
  'team_attrition_trend',
  'average_team_tenure',
  'team_tenure_dispersion',
  'team_workload_pressure',
  'team_internal_mobility_rate',
]);

function factorCategory(factorKey) {
  return CATEGORY_BY_FACTOR[factorKey] || 'org_context';
}

function primaryDriverLabel(factorKey) {
  const promotionCategoryLabels = new Set([
    'time_since_last_promotion',
    'career_velocity_vs_peers',
    'promotion_path_clarity',
  ]);
  if (promotionCategoryLabels.has(factorKey)) {
    return PRIMARY_LABEL_BY_CATEGORY.progression;
  }
  const cat = factorCategory(factorKey);
  if (cat === 'progression') {
    return managerFactorLabel(factorKey);
  }
  if (PRIMARY_LABEL_BY_CATEGORY[cat]) return PRIMARY_LABEL_BY_CATEGORY[cat];
  return managerFactorLabel(factorKey);
}

function isInferredFactor(factorKey) {
  return INFERRED_FACTOR_KEYS.has(factorKey);
}

module.exports = {
  managerFactorLabel,
  managerPatternTitle,
  factorCategory,
  primaryDriverLabel,
  isInferredFactor,
  FACTOR_LABELS,
  INFERRED_FACTOR_KEYS,
};
